import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';

import { Icon, type IconName } from './Icon';

export type ButtonVariant =
  | 'fill'
  | 'stroke'
  | 'ghost'
  | 'quiet'
  | 'danger'
  | 'primary'
  | 'secondary'
  | 'noFill';

export type ButtonSize = 'large' | 'medium' | 'small' | 'xsmall';
export type ButtonSizeProp = ButtonSize | 'm' | 's' | 'xs';

/** Figma M/S/XS. `large` and `m` are M. The class is only `medium` | `small` | `xsmall`. */
const buttonSizeClass = {
  large: 'medium',
  m: 'medium',
  medium: 'medium',
  s: 'small',
  small: 'small',
  xs: 'xsmall',
  xsmall: 'xsmall',
} as const;

export type ButtonSizeClass = (typeof buttonSizeClass)[ButtonSizeProp];

const variantClass: Record<Exclude<ButtonVariant, 'noFill'>, string> = {
  fill: 'primary',
  primary: 'primary',
  stroke: 'secondary',
  secondary: 'secondary',
  ghost: 'ghost',
  quiet: 'quiet',
  danger: 'danger',
};

export function resolveButtonSize(size: ButtonSizeProp): ButtonSizeClass {
  return buttonSizeClass[size];
}

export function resolveButtonVariant(variant: ButtonVariant): Exclude<ButtonVariant, 'noFill'> {
  return variant === 'noFill' ? 'quiet' : variant;
}

export function buttonIconPixelSize(size: ButtonSizeProp, iconOnly: boolean): number {
  const resolved = resolveButtonSize(size);
  if (resolved === 'xsmall') {
    return 16;
  }
  if (iconOnly) {
    return 20;
  }
  return resolved === 'small' ? 16 : 20;
}

interface CommonProps {
  children?: ReactNode;
  icon?: IconName;
  loading?: boolean;
  size?: ButtonSizeProp;
  trailingIcon?: IconName;
  variant?: ButtonVariant;
}

function buttonClassName({
  className = '',
  iconOnly,
  size,
  variant,
}: {
  className?: string;
  iconOnly: boolean;
  size: ButtonSizeProp;
  variant: ButtonVariant;
}): string {
  const resolvedSize = resolveButtonSize(size);
  const resolvedVariant = resolveButtonVariant(variant);
  return [
    'ui-button',
    `ui-button-${variantClass[resolvedVariant]}`,
    `ui-button-${resolvedSize}`,
    iconOnly ? 'ui-button-icon-only' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

type ButtonProps = CommonProps & ButtonHTMLAttributes<HTMLButtonElement>;

export function Button({
  children,
  className = '',
  disabled,
  icon,
  loading = false,
  size = 'medium',
  trailingIcon,
  variant = 'primary',
  ...props
}: ButtonProps) {
  const iconOnly = Boolean(icon && !children && !trailingIcon);
  const glyphSize = buttonIconPixelSize(size, iconOnly);

  return (
    <button
      aria-busy={loading || undefined}
      className={buttonClassName({ className, iconOnly, size, variant })}
      disabled={disabled || loading}
      {...props}
    >
      {icon ? <Icon className="ui-button-icon" name={icon} size={glyphSize} /> : null}
      {children ? <span className="ui-button-label">{children}</span> : null}
      {trailingIcon ? (
        <Icon className="ui-button-icon ui-button-trailing" name={trailingIcon} size={glyphSize} />
      ) : null}
    </button>
  );
}

type ActionLinkProps = Omit<CommonProps, 'loading'> & LinkProps;

export function ActionLink({
  children,
  className = '',
  icon,
  size = 'medium',
  trailingIcon,
  variant = 'primary',
  ...props
}: ActionLinkProps) {
  const iconOnly = Boolean(icon && !children && !trailingIcon);
  const glyphSize = buttonIconPixelSize(size, iconOnly);

  return (
    <Link className={buttonClassName({ className, iconOnly, size, variant })} {...props}>
      {icon ? <Icon className="ui-button-icon" name={icon} size={glyphSize} /> : null}
      {children ? <span className="ui-button-label">{children}</span> : null}
      {trailingIcon ? (
        <Icon className="ui-button-icon ui-button-trailing" name={trailingIcon} size={glyphSize} />
      ) : null}
    </Link>
  );
}
